import { useLoadDashboard } from "@/artifacts/admin/hooks/dashboard/useLoadDashboard";
import { useManageStudents } from "@/artifacts/admin/hooks/dashboard/useManageStudents";
import type {
  AttendanceData,
  MissingEnrollmentData,
} from "@/artifacts/admin/pages/Dashboard";
import { VStack } from "@/components/layout/VStack";
import { Button } from "@/components/ui/Button/Button";
import { Input } from "@/components/ui/Input/Input";
import { Label } from "@/components/ui/Label/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select/Select";
import { Separator } from "@/components/ui/Separator/Separator";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/Sheet/Sheet";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import { Switch } from "@/components/ui/Switch/Switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import {
  getRecencyCutoff,
  isRecentRotation,
  isRecentSession,
} from "@/utils/rotations/recency";
import { format } from "date-fns";
import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { ExplorationSearchSelect } from "./ExplorationSearchSelect";

const ManageStudentSheet = ({
  student,
  open,
  onOpenChange,
  sessionsData,
  teachers,
  loading,
  setMissingEnrollment,
  apiBase = "admin",
  // setAttendance,
}: {
  student: {
    _id: string;
    name: string;
    email: string;
    gradeLevel: string;
    advisor?: string | null;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionsData: {
    rotations: any[];
    sessions: any[];
  } | null;
  teachers: any[] | null;
  loading: boolean;
  setMissingEnrollment?: Dispatch<
    SetStateAction<MissingEnrollmentData[] | null>
  >;
  setAttendance?: Dispatch<SetStateAction<AttendanceData | null>>;
  apiBase?: "admin" | "advisor";
}) => {
  const [advisorId, setAdvisorId] = useState<string>(student.advisor ?? "");
  const [selected, setSelected] = useState<{
    rotation: string;
    session: string;
  }>({ rotation: "", session: "" });
  const [studentSession, setStudentSession] = useState<{
    enrollment: any;
    preferences: any;
    attendance: any[];
    explorations: any[];
    classes: any[];
  } | null>(null);
  const [updated, setUpdated] = useState<{
    enrollmentExploration: string;
    preferenceChoices: [string, string, string];
    attendance: { classId: string; present: boolean; reason?: string }[];
  } | null>(null);

  const resetState = () => {
    setSelected({ rotation: "", session: "" });
    setStudentSession(null);
    setUpdated(null);
    setAdvisorId(student.advisor ?? "");
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetState();
    onOpenChange(nextOpen);
  };

  const { loadStudentSession, loading: loadingStudentSession } =
    useLoadDashboard({ apiBase });

  const {
    saveEnrollment,
    removeEnrollment,
    savePreferences,
    removePreferences,
    saveAttendance,
    saveAdvisor,
    loading: loadingUpdate,
  } = useManageStudents({ apiBase });

  const cutoff = useMemo(() => getRecencyCutoff(), []);

  const recentRotations = useMemo(() => {
    const all = sessionsData?.rotations ?? [];
    return all.filter(
      (r) => isRecentRotation(r, cutoff) || r._id === selected.rotation,
    );
  }, [sessionsData?.rotations, cutoff, selected.rotation]);

  const filteredSessions = useMemo(() => {
    const all = sessionsData?.sessions ?? [];
    const allRotations = sessionsData?.rotations ?? [];
    if (!selected.rotation) return [];
    return all.filter(
      (s) =>
        String(s.rotation) === String(selected.rotation) &&
        (isRecentSession(s, allRotations, cutoff) ||
          s._id === selected.session),
    );
  }, [
    sessionsData?.sessions,
    sessionsData?.rotations,
    selected.rotation,
    selected.session,
    cutoff,
  ]);

  const takenByIndex = useMemo(() => {
    const choices = updated?.preferenceChoices ?? ["", "", ""];
    return [0, 1, 2].map((idx) => {
      const s = new Set<string>();
      choices.forEach((v, i) => {
        if (i !== idx && v) s.add(v);
      });
      return s;
    }) as [Set<string>, Set<string>, Set<string>];
  }, [updated?.preferenceChoices]);

  const setPref = (idx: 0 | 1 | 2, val: string) => {
    setUpdated((prev) => {
      if (!prev) return prev;
      const next = [...prev.preferenceChoices] as [string, string, string];
      next[idx] = val;
      return { ...prev, preferenceChoices: next };
    });
  };

  const setAttendancePresent = (classId: string, present: boolean) => {
    setUpdated((prev) => {
      if (!prev) return prev;

      const index = prev.attendance.findIndex((a) => a.classId === classId);

      if (index === -1) {
        return {
          ...prev,
          attendance: [...prev.attendance, { classId, present }],
        };
      }

      const next = prev.attendance.slice();
      next[index] = { ...next[index], present, classId };
      return { ...prev, attendance: next };
    });
  };

  const setAttendanceReason = (classId: string, reason?: string) => {
    setUpdated((prev) => {
      if (!prev) return prev;

      const index = prev.attendance.findIndex((a) => a.classId === classId);

      if (index === -1) {
        return {
          ...prev,
          attendance: [...prev.attendance, { classId, present: false, reason }],
        };
      }

      const next = prev.attendance.slice();
      next[index] = { ...next[index], reason, classId };
      return { ...prev, attendance: next };
    });
  };

  const onSaveEnrollment = async () => {
    const result = await saveEnrollment(
      student._id,
      selected.session,
      updated?.enrollmentExploration,
    );
    if (!result) return;
    setMissingEnrollment?.((prev) => {
      if (!prev) return null;
      return prev.map((r) => ({
        ...r,
        sessions: r.sessions.map((s) => {
          if (s._id !== result.enrollment.session) return s;
          return {
            ...s,
            missingEnrollments: s.missingEnrollments.filter(
              (m) => m._id !== result.enrollment.student,
            ),
          };
        }),
      }));
    });
  };

  const onRemoveEnrollment = async () => {
    const result = await removeEnrollment(student._id, selected.session);
    if (!result) return;
    setMissingEnrollment?.((prev) => {
      if (!prev) return null;
      return prev.map((r) => ({
        ...r,
        sessions: r.sessions.map((s) => {
          if (s._id !== result.enrollment.session) return s;
          return {
            ...s,
            missingEnrollments: [...s.missingEnrollments, student],
          };
        }),
      }));
    });
  };

  const onSavePreferences = async () => {
    const result = await savePreferences(
      student._id,
      selected.session,
      updated?.preferenceChoices,
    );
    if (!result) return;
    const { hasEnrollment, preferences } = result;

    setMissingEnrollment?.((prev) => {
      if (!prev) return null;
      return prev.map((r) => ({
        ...r,
        sessions: r.sessions.map((s) => {
          if (s._id !== preferences.session) return s;

          const missingPreferences = s.missingPreferences.filter(
            (m) => m._id !== preferences.student,
          );

          const missingEnrollments = hasEnrollment
            ? s.missingEnrollments
            : s.missingEnrollments.some((m) => m._id === preferences.student)
              ? s.missingEnrollments
              : [
                  ...s.missingEnrollments,
                  {
                    _id: student._id,
                    name: student.name,
                    gradeLevel: student.gradeLevel,
                  },
                ];

          return {
            ...s,
            missingPreferences,
            missingEnrollments,
          };
        }),
      }));
    });
  };

  const onRemovePreferences = async () => {
    const result = await removePreferences(student._id, selected.session);
    if (!result) return;
    setMissingEnrollment?.((prev) => {
      if (!prev) return null;
      return prev.map((r) => ({
        ...r,
        sessions: r.sessions.map((s) => {
          if (s._id !== result.preferences.session) return s;
          return {
            ...s,
            missingPreferences: [...s.missingPreferences, student],
          };
        }),
      }));
    });
  };

  const onSaveAttendance = async () => {
    await saveAttendance(student._id, selected.session, updated?.attendance);
  };

  const onSaveAdvisor = async () => {
    await saveAdvisor(student._id, advisorId === "" ? null : advisorId);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>Manage {student.name}</SheetTitle>
          <SheetDescription>
            Edit enrollment, preferences, and attendance records for{" "}
            {student.name} for a selected rotation and session.
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          {apiBase === "admin" && (
            <>
              <VStack gap="2px">
                <Label>Advisor</Label>
                <Select
                  value={advisorId === "" ? "__none__" : advisorId}
                  onValueChange={(val) =>
                    setAdvisorId(val === "__none__" ? "" : val)
                  }
                  disabled={!teachers}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        !teachers ? "Loading advisors..." : "Select advisor"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No advisor</SelectItem>
                    {(teachers ?? []).map((t) => (
                      <SelectItem key={t._id} value={t._id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </VStack>
              <Button
                onClick={onSaveAdvisor}
                disabled={
                  loadingUpdate.advisor ||
                  advisorId === (student.advisor ?? "")
                }
                style={{ width: "100%" }}
              >
                Save Advisor
              </Button>
              <Separator />
            </>
          )}
          <VStack gap="2px">
            <Label>Rotation</Label>
            <Select
              disabled={loading}
              value={selected.rotation}
              onValueChange={(val) =>
                setSelected((prev) => ({ ...prev, rotation: val }))
              }
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    loading ? "Loading rotations..." : "Select rotation"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {recentRotations.length < 1 ? (
                  <SelectItem disabled value=" ">
                    No rotations found
                  </SelectItem>
                ) : (
                  recentRotations.map((rotation) => (
                    <SelectItem key={rotation._id} value={rotation._id}>
                      {format(rotation.date, "MMMM dd, yyyy")}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </VStack>
          <VStack gap="2px">
            <Label>Session</Label>
            <Select
              disabled={loading || !selected.rotation}
              value={selected.session}
              onValueChange={async (val) => {
                setSelected((prev) => ({ ...prev, session: val }));
                await loadStudentSession(
                  student._id,
                  val,
                  setStudentSession,
                  setUpdated,
                );
              }}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    loading
                      ? "Loading sessions..."
                      : !selected.rotation
                        ? "Select rotation first"
                        : "Select session"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {!selected.rotation ? (
                  <SelectItem disabled value=" ">
                    Select rotation first
                  </SelectItem>
                ) : filteredSessions.length < 1 ? (
                  <SelectItem disabled value=" ">
                    No sessions for this rotation
                  </SelectItem>
                ) : (
                  filteredSessions.map((session) => (
                    <SelectItem key={session._id} value={session._id}>
                      {session.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </VStack>
          {loadingStudentSession.studentSession && (
            <Skeleton style={{ width: "100%", height: "98px" }} />
          )}
          {studentSession && (
            <>
              <Separator />
              <VStack>
                <VStack gap="2px">
                  <Label>Enrollment</Label>
                  <ExplorationSearchSelect
                    explorations={studentSession.explorations}
                    teachers={teachers}
                    value={updated?.enrollmentExploration ?? ""}
                    onChange={(val) => {
                      setUpdated((prev) => {
                        if (!prev) return prev;
                        return { ...prev, enrollmentExploration: val };
                      });
                    }}
                    disabled={!selected.session}
                  />
                </VStack>
                <Button
                  onClick={onSaveEnrollment}
                  disabled={
                    loadingUpdate.enrollment ||
                    !selected.session ||
                    !updated?.enrollmentExploration
                  }
                  style={{ width: "100%" }}
                >
                  Save Enrollment
                </Button>
                <Button
                  onClick={onRemoveEnrollment}
                  variant="destructive"
                  disabled={
                    loadingUpdate.removeEnrollment ||
                    !selected.session ||
                    !studentSession.enrollment
                  }
                  style={{ width: "100%" }}
                >
                  Remove Enrollment
                </Button>
              </VStack>
              {!sessionsData?.sessions.find((s) => s._id === selected.session)
                .singleton && (
                <VStack>
                  <VStack>
                    <Label>Preferences</Label>
                    {[0, 1, 2].map((idx) => {
                      const taken = takenByIndex[idx as 0 | 1 | 2];

                      return (
                        <VStack key={idx} gap="2px">
                          <Label>Choice {idx + 1}</Label>
                          <ExplorationSearchSelect
                            explorations={studentSession.explorations}
                            teachers={teachers}
                            value={updated?.preferenceChoices[idx] ?? ""}
                            onChange={(val) => {
                              setPref(idx as 0 | 1 | 2, val);
                            }}
                            disabled={!selected.session}
                            disabledIds={taken}
                          />
                        </VStack>
                      );
                    })}
                  </VStack>
                  <Button
                    onClick={onSavePreferences}
                    disabled={
                      loadingUpdate.preferences ||
                      !selected.session ||
                      !updated?.preferenceChoices ||
                      updated?.preferenceChoices.length < 3
                    }
                    style={{ width: "100%" }}
                  >
                    Save Preferences
                  </Button>
                  <Button
                    onClick={onRemovePreferences}
                    variant="destructive"
                    disabled={
                      loadingUpdate.removePreferences ||
                      !selected.session ||
                      !studentSession.preferences
                    }
                    style={{ width: "100%" }}
                  >
                    Remove Enrollment
                  </Button>
                </VStack>
              )}
              {apiBase === "admin" && (
              <VStack>
                <Label>Attendance</Label>
                <Table borderColor="var(--secondary-border)">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Present</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {studentSession.classes.length < 1 ? (
                      <TableRow>
                        <TableCell colSpan={3}>No classes found</TableCell>
                      </TableRow>
                    ) : (
                      studentSession.classes.map((c) => {
                        const row = updated?.attendance.find(
                          (a) => a.classId === String(c._id),
                        );

                        return (
                          <TableRow key={c._id}>
                            <TableCell>
                              {format(c.date, "MMMM dd, yyyy")}
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={row?.present ?? false}
                                onCheckedChange={(val) =>
                                  setAttendancePresent(c._id, val)
                                }
                                disabled={!updated}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={row?.reason ?? ""}
                                onChange={(e) =>
                                  setAttendanceReason(c._id, e.target.value)
                                }
                                placeholder="Reason"
                                disabled={!updated}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                <Button
                  onClick={onSaveAttendance}
                  disabled={
                    loadingUpdate.attendance ||
                    !selected.session ||
                    !updated?.attendance ||
                    updated?.attendance.length < 1
                  }
                  style={{ width: "100%" }}
                >
                  Save Attendance
                </Button>
              </VStack>
              )}
            </>
          )}
        </SheetBody>
        <SheetFooter>
          <SheetClose asChild>
            <Button variant="outline" style={{ width: "100%" }}>
              Close
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export { ManageStudentSheet };
